import React, { useMemo, useState } from 'react';
import {
  TableConfiguration,
  TableModule,
} from '@lifeomic/chroma-react/components/TableModule';

import { Button } from '@lifeomic/chroma-react/components/Button';
import ModuleHeader from '../../Table/ModuleHeader';
import { IconButtonLink } from '@lifeomic/chroma-react/components/IconButtonLink';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import { defineMessages, useIntl } from 'react-intl';
import { Code, Sparkle, SparkleRefresh, Trash } from '@lifeomic/chromicons';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { makeStyles, Theme } from '@lifeomic/chroma-react/styles';

import { SingleFhirSearchResult } from '../../../hooks/SearchService';
import { ImpressionItem } from '../ImpressionEditor/ImpressionItem';
import { ReportEditor } from './ReportEditor';
import { useTemplatesContext } from './Context';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import urls from '../../../../../util/urls';
import { useLocalStorageState } from '../../../../../hooks';
import EmptyPageState from '../../../../EmptyPageState';
import { GenerateModal } from './GenerateModal';

export const useStyles = makeStyles((theme: Theme) => ({
  bkgd: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    background: theme.palette.black[50],
    height: '100%',
  },
  emptyStateContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    padding: theme.spacing(1),
  },
  emptyState: {
    background: theme.palette.common.white,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    width: '100%',
  },
  tableRootContainer: {
    overflow: 'hidden',
    padding: theme.spacing(0, 1, 1),
  },
  tableContainer: {
    height: '100%',
    overflow: 'auto',
  },
  loadingTableClass: {
    display: 'flex',
    justifyContent: 'center',
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  editingContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  editingContent: {
    flex: 1,
    overflow: 'hidden',
  },
}));

const messages = defineMessages({
  noReports: {
    id: 'ReportsTable.noReports',
    defaultMessage:
      'This member has no reports yet. Use the templates to start generating reports.',
  },
  description: {
    id: 'ReportsTable.description',
    defaultMessage: 'Description',
  },
  template: {
    id: 'ReportsTable.template',
    defaultMessage: 'Template',
  },
  status: {
    id: 'ReportsTable.status',
    defaultMessage: 'Status',
  },
  lastEdited: {
    id: 'ReportsTable.lastEdited',
    defaultMessage: 'Last Edited',
  },
  lastEditedBy: {
    id: 'ReportsTable.lastEditedBy',
    defaultMessage: 'Last Edited By',
  },
  editReport: {
    id: 'ReportsTable.editReport',
    defaultMessage: 'Edit Report',
  },
  manageTemplate: {
    id: 'ReportsTable.manageTemplate',
    defaultMessage: 'Manage Template',
  },
  deleteReport: {
    id: 'ReportsTable.deleteReport',
    defaultMessage: 'Delete Report',
  },
  areYouSure: {
    id: 'ReportsTable.areYouSure',
    defaultMessage: 'Are you sure you want to delete report "{reportName}"?',
  },
  cancel: {
    id: 'ReportsTable.cancel',
    defaultMessage: 'Cancel',
  },
  confirm: {
    id: 'ReportsTable.confirm',
    defaultMessage: 'Confirm',
  },
  regenerateReport: {
    id: 'ReportsTable.regenerateReport',
    defaultMessage: 'Regenerate Report',
  },
  regenerating: {
    id: 'ReportsTable.regenerating',
    defaultMessage: 'Regenerating...',
  },
  generateNewReport: {
    id: 'ReportsTable.generateNewReport',
    defaultMessage: 'Generate New Report',
  },
});

export const ReportsTable = ({
  patient,
}: {
  patient: fhir.Patient & { id: string };
}) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const {
    reports,
    deleteReport,
    refetchReports,
    fetchMoreReports,
    hasMoreReports,
    loadingReports,
    invocations,
  } = useTemplatesContext();

  const [searchValue, setSearchValue] = useState('');
  const [deleting, setDeleting] = useState<ImpressionItem | undefined>(
    undefined,
  );

  const isFetching = useMemo(() => {
    return (
      loadingReports ||
      invocations.some(invocation => invocation.status === 'processing')
    );
  }, [loadingReports, invocations]);

  const [triggeringGeneration, setTriggeringGeneration] = useState<
    ImpressionItem | undefined
  >(undefined);

  const searchResult: Omit<SingleFhirSearchResult, 'resourceType'> =
    useMemo(() => {
      return {
        nextLink: '',
        results: reports.map(report => ({
          resource: report,
          fullUrl: '',
        })),
      };
    }, [reports]);

  const confirmDelete = () => {
    if (deleting && deleting.id && deleting.status !== 'completed') {
      deleting.impression.status = 'entered-in-error';

      deleteReport(
        deleting.impression as fhir.ClinicalImpression & { id: string },
      );
      setDeleting(undefined);
    }
  };

  const tableData: ImpressionItem[] = useMemo(() => {
    return reports.map(report => new ImpressionItem(report));
  }, [reports]);

  const filteredTableData = useMemo(() => {
    return tableData.filter(row =>
      row.title?.toLowerCase().includes(searchValue.toLowerCase()),
    );
  }, [tableData, searchValue]);

  // keep track of the selected report so it re-opens on navigation
  const [selectedReportId, setSelectedReportId] = useLocalStorageState<
    string | null
  >('ai-tools-selected-report-id', null);

  const selectedReport = useMemo(() => {
    return tableData.find(row => row.id === selectedReportId);
  }, [tableData, selectedReportId]);

  const tableConfig: TableConfiguration[] = useMemo(
    () => [
      {
        header: {
          label: formatMessage(messages.description),
        },
        cell: {
          valuePath: 'title',
        },
      },
      {
        header: {
          label: formatMessage(messages.lastEdited),
        },
        cell: {
          valuePath: 'lastEdited',
        },
      },
      {
        header: {
          label: formatMessage(messages.lastEditedBy),
        },
        cell: {
          valuePath: 'lastEditedBy',
        },
      },
      {
        header: {
          label: formatMessage(messages.status),
        },
        cell: {
          content: (row: ImpressionItem) => {
            const templateId = row.templateId;
            if (!row.aiGenerated) {
              return <div>{row.status}</div>;
            }
            const invocation = invocations.find(
              i =>
                i.agentInput.template_id === templateId &&
                i.status === 'processing',
            );
            if (!invocation) {
              return <div>{row.status}</div>;
            }
            return (
              <Tooltip title={formatMessage(messages.regenerating)}>
                <DotLoader size={0} />
              </Tooltip>
            );
          },
        },
      },
    ],
    [invocations, formatMessage],
  );

  const deleteModel = (
    <Modal
      isOpen={!!deleting}
      onDismiss={() => setDeleting(undefined)}
      title={formatMessage(messages.deleteReport, {
        reportName: deleting?.title,
      })}
      size={1}
      fullWidth
      actions={
        <>
          <Button variant="text" onClick={() => setDeleting(undefined)}>
            {formatMessage(messages.cancel)}
          </Button>
          <Button onClick={confirmDelete}>
            {formatMessage(messages.confirm)}
          </Button>
        </>
      }
    >
      {formatMessage(messages.areYouSure, {
        reportName: deleting?.title,
      })}
    </Modal>
  );

  const rowActions = useMemo(() => {
    return (data: ImpressionItem) => {
      return (
        <>
          {data.aiGenerated && data.status !== 'completed' && (
            <Tooltip title={formatMessage(messages.regenerateReport)}>
              <IconButton
                aria-label={formatMessage(messages.regenerateReport)}
                color="inverse"
                icon={SparkleRefresh}
                onClick={e => {
                  setTriggeringGeneration(data);
                  e.stopPropagation();
                }}
                size={0}
              />
            </Tooltip>
          )}
          {(!data.aiGenerated || data.status === 'completed') && (
            <Tooltip title={formatMessage(messages.generateNewReport)}>
              <IconButton
                aria-label={formatMessage(messages.generateNewReport)}
                color="inverse"
                icon={Sparkle}
                onClick={e => {
                  setTriggeringGeneration(data);
                  e.stopPropagation();
                }}
              />
            </Tooltip>
          )}
          <Tooltip title={formatMessage(messages.manageTemplate)}>
            <IconButtonLink
              aria-label={formatMessage(messages.manageTemplate)}
              color="inverse"
              icon={Code}
              size={0}
              onClick={e => {
                e.stopPropagation();
              }}
              to={urls.app.workflows.reportTemplates(data.templateId)}
            />
          </Tooltip>

          {data.status !== 'completed' && (
            <Tooltip title={formatMessage(messages.deleteReport)}>
              <IconButton
                aria-label={formatMessage(messages.deleteReport)}
                title={formatMessage(messages.deleteReport)}
                color="inverse"
                icon={Trash}
                onClick={e => {
                  e.stopPropagation();
                  setDeleting(data);
                }}
              />
            </Tooltip>
          )}
        </>
      );
    };
  }, [formatMessage]);

  // If a report is selected, show the editing UI
  if (selectedReport) {
    return (
      <div className={classes.root}>
        <div className={classes.editingContainer}>
          <div className={classes.editingContent}>
            <ReportEditor
              impression={selectedReport}
              patient={patient}
              refresh={refetchReports}
              onClose={() => {
                setSelectedReportId(null);
                refetchReports();
              }}
              onDelete={() => {
                setDeleting(selectedReport);
                refetchReports();
              }}
            />
          </div>
        </div>
        {deleteModel}
      </div>
    );
  }
  if (!isFetching && reports.length === 0) {
    return (
      <div className={classes.emptyStateContainer}>
        <div className={classes.emptyState}>
          <EmptyPageState
            message={formatMessage(messages.noReports)}
            illustration={Sparkle}
          />
        </div>
      </div>
    );
  }
  return (
    <div className={classes.bkgd}>
      <GenerateModal
        isOpen={!!triggeringGeneration}
        onDismiss={() => setTriggeringGeneration(undefined)}
        templateId={triggeringGeneration?.templateId || ''}
        activeImpression={triggeringGeneration}
      />
      <ModuleHeader
        isFetching={isFetching}
        searchResult={searchResult}
        hasMoreToLoad={!!hasMoreReports}
        loadMore={() => {
          fetchMoreReports();
          return Promise.resolve();
        }}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
      />
      <div className={classes.tableRootContainer}>
        <div className={classes.tableContainer}>
          <TableModule
            config={tableConfig}
            data={filteredTableData}
            isLoading={
              loadingReports ||
              invocations.some(invocation => invocation.status === 'processing')
            }
            onRowClick={(data: ImpressionItem) => {
              setSelectedReportId(data.id);
            }}
            rowClickLabel={formatMessage(messages.editReport)}
            rowActions={rowActions}
          />
        </div>
      </div>
      {deleteModel}
    </div>
  );
};
